(ns dashboard.components.table
  (:require
   [clojure.string :refer [split lower-case]]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]
   [headlessui-reagent.core :as ui]
   [dashboard.i18n :refer [tr]]
   [dashboard.utils :refer [listen href index-by cs]]
   ["@heroicons/react/solid" :refer
    [ChevronDownIcon ChevronUpIcon ChevronLeftIcon ChevronRightIcon]]))

(def sort-icons
  {:default {:asc ChevronUpIcon
             :desc ChevronDownIcon}
   :numerical {:asc "sort-numerical"
               :desc "sort-numerical-desc"}
   :alphabetical {:asc "sort-alphabetical"
                  :desc "sort-alphabetical-desc"}})

(def default-sorting
  {:disabled false
   :icons (:default sort-icons)
   :comparator >})

(defn sorting-comparator [header dir]
  (let [sort-opts (merge default-sorting (get header :sorting {}))
        cmp (sort-opts :comparator)
        cmp-fn (if (= :renderer (sort-opts :comparator))
                 (let [render (get header :render-cell identity)]
                   #(< (render %1) (render %2)))
                 cmp)]
    (cond->> cmp-fn
      (= :desc dir) complement)))

(defn sort-rows [sorting headers rows]
  (let [{:keys [key dir]} sorting
        header ((index-by headers :key {:convert-fn keyword}) key)
        comparator-fn (sorting-comparator header dir)]
    (sort-by key comparator-fn rows)))

(defn th [{:keys [header sorting condensed?]}]
  [:th.text-left.text-sm.font-semibold.text-gray-900.whitespace-nowrap
   {:scope "col"
    :class (cs (or (:class header) "")
               (if condensed? "px-2 py-2.5" "px-3 py-3.5"))
    :style (:style header)}
   [:a.group.inline-flex
    (:title header)
    (when (some? (:sorting header))
      [:span.ml-2.flex-none.rounded.bg-gray-200.text-gray-900.group-hover:bg-gray-300
       (let [{sort-key :key dir :dir} @sorting
             sorting-opts (merge default-sorting (:sorting header))
             icons (sorting-opts :icons)
             key (:key header)
             icon (if (= key (:key @sorting))
                    (dir icons)
                    (:asc icons))]
         [:> icon
          {:aria-hidden "true"
           :class "h-5 w-5"
           :on-click #(swap! sorting
                             (fn [{k :key d :dir}]
                               (if (and (= k key) (= :desc d))
                                 {:key key
                                  :dir :asc}
                                 {:key key
                                  :dir (if (= k key) :desc :asc)})))}])])]])

(defn td [{:keys [value row header condensed?]}]
  [:td.whitespace-nowrap.text-sm.text-gray-900
   {:class (cs ""
               (if condensed? "px-2 py-2" "px-3 py-4"))}
   (if (fn? (:render-cell header))
     [(:render-cell header) value row]
     value)])

(defn- table-content []
  (let [sorting (reagent/atom {})]
    (fn [{:keys [headers rows actions group-by-fn condensed?]
          :or {group-by-fn (constantly nil)
               condensed? false}}]
      (let [rows (cond->> rows
                   (seq @sorting) (sort-rows @sorting headers))]
        [:div.-my-2.-mx-4.overflow-x-auto.sm:-mx-6.lg:-mx-8
         [:div.inline-block.min-w-full.py-2.align-middle.md:px-6.lg:px-8
          [:div.overflow-hidden.shadow.ring-1.ring-black.ring-opacity-5.md:rounded-lg
           [:table.min-w-full.divide-y.divide-gray-300
            ;; Header
            [:thead.bg-gray-50
             [:tr
              (doall
               (for [head headers]
                 ^{:key (str (:key head) "-header")}
                 [th
                  {:condensed? condensed?
                   :header head
                   :sorting sorting}]))
              (when (some? actions)
                [:th.relative.pl-3.pr-4.sm:pr-6.text-sm.font-semibold.text-right
                 {:class (if condensed? "px-1 py-2.5" "px-3 py-3.5")
                  :scope "col"}
                 [:span.sr-only (tr :action/edit)]])]]

            ;; Body
            [:tbody.divide-y.divide-gray-200.bg-white
             ;; Group
             (doall
              (for [[group grouped-rows] (group-by group-by-fn rows)]
                ^{:key (str (or group (gensym)) "-group")}
                [:<>
                 (when (some? group)
                   [:tr.border-t.border-gray-200
                    [:th.bg-gray-50.px-4.py-2.text-left.text-sm.font-semibold.text-gray-900.sm:px-6
                     {:colSpan (cond-> (count headers)
                                 (some? actions) inc)
                      :scole "colgroup"}
                     group]])

                 (if (not (seq grouped-rows))
                   [:tr
                    [:td
                     {:colSpan (cond-> (count headers)
                                 (some? actions) inc)}
                     [:span.px-2.py-4.leading-10.text-gray-500
                      "No entries to show"]]]
                   (doall
                    (for [row grouped-rows]
                      ^{:key (str (or (or (:id row) (:uuid row)) (gensym)) "-row")}
                      [:tr
                       (doall
                        (for [head headers]
                          ^{:key (or (str (:uuid row) "-" (:key head) "-cell") (gensym))}
                          [td
                           {:value (get row (:key head))
                            :row row
                            :header head
                            :condensed? condensed?}]))

                       ;; Actions
                       (when (some? actions)
                         [:td.relative.whitespace-nowrap.pl-3.pr-4.text-right.text-sm.font-medium.sm:pr-6.space-x-1
                          {:class (if condensed? "px-2 py-2" "px-3 py-4")}
                          (doall
                           (for [action actions]
                             ^{:key (gensym)}
                             [:button.inline-flex.items-center.rounded-md.border.border-gray-300.bg-white.font-medium.leading-4.text-gray-700.shadow-sm.hover:bg-gray-50.focus:outline-none.focus:ring-2.focus:ring-primary-500.focus:ring-offset-2.disabled:cursor-not-allowed.disabled:opacity-30
                              {:type "button"
                               :class (if condensed? "px-1 py-1 text-xs" "px-2 py-2 text-sm")
                               :on-click (when (some? (:on-click action))
                                           (fn [] ((:on-click action) row)))}
                              (when (some? (:icon action))
                                [:> (:icon action)
                                 {:class "h-5 w-5"}])
                              (when (some? (:text action))
                                [:a.text-primary-600.hover:text-primary-900
                                 (if (fn? (:text action))
                                   ((:text action) row)
                                   (:text action))])]))])])))]))]]]]]))))

(defn table [{:keys [headers rows actions group-by-fn condensed?]
              :or {group-by-fn (constantly nil)
                   condensed? false}}]
  [table-content
   {:headers headers
    :rows rows
    :actions actions
    :group-by-fn group-by-fn
    :condensed? condensed?}])

(defn pagination []
  [:div.bg-white.py-3.flex.items-center.justify-between.border-t.border-gray-200
   [:div.flex-1.flex.justify-between.sm:hidden
    [:a.relative.inline-flex.items-center.px-4.py-2.border.border-gray-300.text-sm.font-medium.rounded-md.text-gray-700.bg-white.hover:bg-gray-50
     {:href "#"}
     (tr :action/previous)]
    [:a.relative.inline-flex.items-center.px-4.py-2.border.border-gray-300.text-sm.font-medium.rounded-md.text-gray-700.bg-white.hover:bg-gray-50
     {:href "#"}
     (tr :action/previous)]]
   [:div.hidden.sm:flex-1.sm:flex.sm:items-center.sm:justify-between
    [:div
     [:p.text-sm.text-gray-700.space-x-1
      [:span "Showing"]
      [:span.font-medium "1"]
      [:span "to"]
      [:span.font-medium "10"]
      [:span "of"]
      [:span.font-medium "97"]
      [:span "results"]]]]
   [:div
    [:nav.relative.z-0.inline-flex.rounded-md.shadow-sm.-space-x-px
     {:aria-label "Pagination"}
     [:a.relative.inline-flex.items-center.px-2.py-2.rounded-l-md.border.border-gray-300.bg-white.text-sm.font-medium.text-gray-500.hover:bg-gray-50
      {:href "#"}
      [:span.sr-only (tr :action/previous)]
      [:> ChevronLeftIcon
       {:class "h-5 w-5"
        :aria-hidden true}]]
     (doall
      (for [i (range 5)]
        ^{:key (str "page-" i)}
        [:a.bg-white.border-gray-300.text-gray-500.hover:bg-gray-50.relative.inline-flex.items-center.px-4.py-2.border.text-sm.font-medium
         {:href "#"
          :aria-current "page"}
         i]))
     [:a.relative.inline-flex.items-center.px-2.py-2.rounded-r-md.border.border-gray-300.bg-white.text-sm.font-medium.text-gray-500.hover:bg-gray-50
      {:href "#"}
      [:span.sr-only
       (tr :action/next)]
      [:> ChevronRightIcon
       {:class "h-5 w-5"
        :aria-hidden true}]]]]])
